const API_BASE_URL = import.meta.env.VITE_API_URL || "";

interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
  meta?: {
    page: number;
    limit: number;
    total: number;
    timestamp: number;
  };
}

interface FetchApiOptions extends RequestInit {
  timeoutMs?: number;
}

export interface QuadrantPoint {
  id: string;
  symbol: string;
  interval: string;
  rsi: number;
  score: number;
  zone: string;
  price: number;
  change24h: number;
  volume24h: number;
  divergenceStrength: number;
  detectedAt: string;
  lastUpdate: string;
}

export interface KlineCandle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface IndicatorData {
  time: number;
  rsi: number;
  macd: number;
  macdSignal: number;
  macdHistogram: number;
}

export interface DivergencePoint {
  time: number;
  type: string;
  wave: number;
  priceLevel: number;
  rsiLevel: number;
  strength: number;
}

export interface KlineDataResponse {
  symbol: string;
  interval: string;
  candles: KlineCandle[];
  indicators: IndicatorData[];
  divergences: DivergencePoint[];
}

async function fetchApi<T>(
  endpoint: string,
  options?: FetchApiOptions,
): Promise<T> {
  const { timeoutMs, signal, headers, ...restOptions } = options ?? {};
  const controller = new AbortController();
  let timeoutId: number | undefined;

  const abortHandler = () => {
    controller.abort(signal?.reason ?? new DOMException("Aborted", "AbortError"));
  };

  if (timeoutMs) {
    timeoutId = window.setTimeout(() => {
      controller.abort(new Error("timeout"));
    }, timeoutMs);
  }

  if (signal) {
    if (signal.aborted) {
      abortHandler();
    } else {
      signal.addEventListener("abort", abortHandler, { once: true });
    }
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      signal: controller.signal,
      ...restOptions,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const json: ApiResponse<T> = await response.json();

    if (json.code !== 0) {
      throw new Error(json.message || "API Error");
    }

    return json.data;
  } catch (error) {
    if (controller.signal.aborted) {
      const reason = controller.signal.reason;
      if (reason instanceof Error && reason.message === "timeout") {
        throw new Error("timeout");
      }
      if (reason instanceof DOMException && reason.name === "AbortError") {
        throw reason;
      }
      throw new DOMException("Aborted", "AbortError");
    }
    throw error;
  } finally {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
    if (signal) {
      signal.removeEventListener("abort", abortHandler);
    }
  }
}

export const quadrantApi = {
  getPoints: async (params?: {
    interval?: string;
    minScore?: number;
    showNeutral?: boolean;
    zone?: string;
    page?: number;
    limit?: number;
  }): Promise<QuadrantPoint[]> => {
    const searchParams = new URLSearchParams();
    if (params?.interval) searchParams.set("interval", params.interval);
    if (params?.minScore) searchParams.set("minScore", String(params.minScore));
    if (params?.showNeutral !== undefined)
      searchParams.set("showNeutral", String(params.showNeutral));
    if (params?.zone) searchParams.set("zone", params.zone);
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.limit) searchParams.set("limit", String(params.limit));

    const query = searchParams.toString();
    return fetchApi<QuadrantPoint[]>(
      `/api/v1/quadrant/points${query ? `?${query}` : ""}`,
    );
  },

  getPointsOrEmpty: async (params?: {
    interval?: string;
    minScore?: number;
    showNeutral?: boolean;
    zone?: string;
    page?: number;
    limit?: number;
  }): Promise<QuadrantPoint[]> => {
    try {
      return await quadrantApi.getPoints(params);
    } catch {
      return [];
    }
  },
};

export const klineApi = {
  getKline: async (
    symbol: string,
    params?: {
      interval?: string;
      limit?: number;
      startTime?: number;
      endTime?: number;
      signal?: AbortSignal;
      timeoutMs?: number;
    },
  ): Promise<KlineDataResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.interval) searchParams.set("interval", params.interval);
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.startTime)
      searchParams.set("startTime", String(params.startTime));
    if (params?.endTime) searchParams.set("endTime", String(params.endTime));

    const query = searchParams.toString();
    return fetchApi<KlineDataResponse>(
      `/api/v1/kline/${symbol}${query ? `?${query}` : ""}`,
      { signal: params?.signal, timeoutMs: params?.timeoutMs ?? 8000 },
    );
  },
};
